import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { apiPost, prettifyApiError } from "@/shared/api";

/** Oggi in ora italiana (AAAA-MM-GG): il default della data di pagamento. */
function oggiItalia(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome" }).format(new Date());
}

/** «40», «40,5», «40.50» -> 40.5. Null se non è un importo positivo. */
export function leggiImporto(testo: string): number | null {
  const pulito = testo.trim().replace(/\s|€/g, "").replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(pulito)) return null;
  const valore = Number(pulito);
  return valore > 0 ? valore : null;
}

type Props = { orderRef: string; onClose: () => void; onSaved?: () => void };

/** Vendita extra (Davide 02/11/2026): il cliente paga a parte, fuori dal
 *  marketplace, qualcosa in più legato all'ordine (es. kit ferramenta a
 *  bonifico). OM crea una riga in Fatturazione con un documento SEPARATO,
 *  intestato come l'ordine; la emette il giro delle 18. */
export function VenditaExtraDialog({ orderRef, onClose, onSaved }: Props) {
  const [importo, setImporto] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [data, setData] = useState(oggiItalia);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fatto, setFatto] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const valore = leggiImporto(importo);
  const canSubmit = valore !== null && descrizione.trim().length >= 3 && data !== "" && !busy;

  async function handleSubmit() {
    if (!canSubmit || valore === null) return;
    setBusy(true);
    setError(null);
    try {
      const riga = await apiPost<{ order_ref: string; doc_type: string | null }>(
        "/api/invoicing/extra",
        { order_ref: orderRef, importo: valore, descrizione: descrizione.trim(), data },
      );
      setFatto(
        `Riga ${riga.order_ref} creata in Fatturazione (${riga.doc_type ?? "documento"}). ` +
          "La emette il giro delle 18.",
      );
      onSaved?.();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }

  const input =
    "h-[40px] rounded-md border border-line bg-card px-3 text-[13px] text-ink outline-none focus:border-ink disabled:opacity-50";

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Vendita extra"
      data-no-open
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        className="w-full max-w-md rounded-lg border border-line bg-card p-5 shadow-popover"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <h3 className="font-serif text-[16px] font-medium text-ink">Vendita extra</h3>
        <p className="mt-1 text-[12px] text-ink">
          Ordine {orderRef}. Il cliente ha pagato a parte, fuori dal marketplace. Si fattura con un
          documento nuovo, intestato come l'ordine.
        </p>

        {fatto ? (
          <p role="status" className="mt-4 rounded-md bg-teal/10 px-3 py-2 text-[13px] text-ink">
            {fatto}
          </p>
        ) : (
          <div className="mt-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink">
              Cosa ha comprato
              <input
                value={descrizione}
                maxLength={100}
                disabled={busy}
                autoFocus
                placeholder="Kit ferramenta letto contenitore"
                onChange={(e) => setDescrizione(e.target.value)}
                className={input}
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink">
                Importo pagato (€, IVA inclusa)
                <input
                  value={importo}
                  inputMode="decimal"
                  disabled={busy}
                  placeholder="40,00"
                  onChange={(e) => setImporto(e.target.value)}
                  className={input}
                />
              </label>
              <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink">
                Pagato il
                <input
                  type="date"
                  value={data}
                  max={oggiItalia()}
                  disabled={busy}
                  onChange={(e) => setData(e.target.value)}
                  className={input}
                />
              </label>
            </div>
          </div>
        )}

        {error && (
          <p role="alert" className="mt-3 text-[12px] font-semibold text-red">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-line bg-card px-3 py-2 text-[12px] font-medium text-ink hover:border-ink-4 disabled:opacity-50"
          >
            {fatto ? "Chiudi" : "Annulla"}
          </button>
          {!fatto && (
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-md border border-teal bg-teal px-3 py-2 text-[12px] font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? "Creo…" : "Crea riga in Fatturazione"}
            </button>
          )}
        </div>
      </form>
    </div>,
    document.body,
  );
}
